import { COLORS } from '../constants/theme';
import { loadImageBase64 } from '../utils/imageLoader';
import { formatPrice } from '../utils/priceFormatter';
import { getProductSizeRange } from '../../../../ReusableComponents/functions';

export const groupCartItemsBySku = (cartItems = []) => {
    const groupedItems = {};
    cartItems.forEach(item => {
        if (!groupedItems[item.sku]) {
            groupedItems[item.sku] = {
                sku: item.sku,
                styleFactory: item.styleFactory,
                color: item.color_on_box || item.Material1,
                thumbnail: item.product_img,
                Price1: item.Price1,
                Price2: item.Price2,
                Price3: item.Price3,
                Price4: item.Price4,
                Price5: item.Price5,
                Size1: item.Size1,
                Size2: item.Size2,
                Size3: item.Size3,
                Size4: item.Size4,
                Size5: item.Size5,
                items: []
            };
        }
        groupedItems[item.sku].items.push(item);
    });
    return Object.values(groupedItems);
};

export const drawSingleProductBlock = async (engine, group) => {
    const doc = engine.getDoc();
    const cursor = engine.getCursor();
    const drawY = cursor.getY();

    const blockWidth = 540;
    const startX = 36;
    const endX = startX + blockWidth; // 576

    const headerHeight = 18; // Row 1 (Style & Color): Gray background
    const skuHeight = 16;    // Row 2 (SKU): White background
    const gridHeight = 38;
    const summaryHeight = 36;
    const blockHeight = headerHeight + skuHeight + gridHeight + summaryHeight; // 108

    const skuRowTop = drawY + headerHeight; // drawY + 18
    const gridTop = skuRowTop + skuHeight;   // drawY + 34
    const gridBottom = gridTop + gridHeight; // drawY + 72
    const gridMiddle = gridTop + (gridHeight / 2); // drawY + 53
    const summaryTop = gridBottom; // drawY + 72

    // 1. Row 1: Style & Color row - Light Gray background (#EAEAEA)
    doc.setFillColor(234, 234, 234);
    doc.rect(startX, drawY, blockWidth, headerHeight, 'F');

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(group.styleFactory || "", startX + 8, drawY + 17);
    doc.text(group.color || "", startX + 105, drawY + 17);

    // 2. Row 2: SKU row - White background (No line separating Row 1 & Row 2)
    doc.setFillColor(255, 255, 255);
    doc.rect(startX, skuRowTop, blockWidth, skuHeight, 'F');

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(group.sku || "", startX + 8, skuRowTop + 12);

    // 3. Image and Size Grid area
    const imgWidth = 60; // Compact left image container matching screenshot
    doc.setFillColor(255, 255, 255);
    doc.rect(startX, gridTop, blockWidth, gridHeight, 'F');

    // Product Image inside box
    const productImgData = await loadImageBase64(group.thumbnail);
    if (productImgData && productImgData.base64) {
        try {
            const maxWidth = imgWidth - 6; // 54
            const maxHeight = gridHeight - 6; // 32
            const ratio = (productImgData.width && productImgData.height)
                ? productImgData.width / productImgData.height
                : 1.5;
            let renderWidth = maxWidth;
            let renderHeight = maxHeight;

            if (ratio > maxWidth / maxHeight) {
                renderWidth = maxWidth;
                renderHeight = maxWidth / ratio;
            } else {
                renderHeight = maxHeight;
                renderWidth = maxHeight * ratio;
            }

            const xOffset = startX + (imgWidth - renderWidth) / 2;
            const yOffset = gridTop + (gridHeight - renderHeight) / 2;

            doc.addImage(productImgData.base64, 'PNG', xOffset, yOffset, renderWidth, renderHeight);
        } catch (err) { }
    }

    // Grid columns setup for product size range
    const productSizes = getProductSizeRange(group);
    const numCols = productSizes.length;
    const sizeGridWidth = blockWidth - imgWidth; // 480
    const colWidth = sizeGridWidth / numCols;

    // Map quantities by size
    const sizeQtyMap = {};
    group.items.forEach(it => {
        sizeQtyMap[it.size] = (sizeQtyMap[it.size] || 0) + it.unitQuantity;
    });

    // Size Labels (Top sub-row)
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(numCols > 20 ? 9 : 9.5);
    doc.setTextColor(0, 0, 0);
    for (let col = 0; col < numCols; col++) {
        const sz = productSizes[col];
        const cx = startX + imgWidth + col * colWidth + (colWidth / 2);
        doc.text(sz.toString(), cx, gridTop + 13.5, { align: 'center' });
    }

    // Quantities (Bottom sub-row) - show blank if 0
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(numCols > 20 ? 9 : 9.5);
    doc.setTextColor(0, 0, 0);
    for (let col = 0; col < numCols; col++) {
        const sz = productSizes[col];
        const qty = sizeQtyMap[sz];
        if (qty && qty > 0) {
            const cx = startX + imgWidth + col * colWidth + (colWidth / 2);
            doc.text(qty.toString(), cx, gridMiddle + 13.5, { align: 'center' });
        }
    }

    // Lines & Dividers - Sharp solid black lines matching screenshot
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.75);

    // Outer rectangle border around whole block
    doc.rect(startX, drawY, blockWidth, blockHeight, 'S');

    // Horizontal dividers (No border line between Row 1 and Row 2)
    doc.line(startX, gridTop, endX, gridTop); // Below SKU row, above size grid row
    doc.line(startX, gridBottom, endX, gridBottom); // Below size grid row, above summary row

    // Grid inner dividers
    doc.line(startX + imgWidth, gridTop, startX + imgWidth, gridBottom); // Vertical line after image container
    doc.line(startX + imgWidth, gridMiddle, endX, gridMiddle); // Horizontal line between sizes and quantities

    // Vertical grid lines for size columns
    doc.setLineWidth(0.5);
    for (let col = 1; col < numCols; col++) {
        const cx = startX + imgWidth + col * colWidth;
        doc.line(cx, gridTop, cx, gridBottom);
    }

    // 4. Bottom Summary Row
    const groupPairs = group.items.reduce((sum, item) => sum + item.unitQuantity, 0);
    const groupTotal = group.items.reduce((sum, item) => sum + (item.unitQuantity * item.unitFinalPrice), 0);

    // Line 1: Size Range: & Pairs
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Size Range:", startX + 8, summaryTop + 14);
    doc.text(`${groupPairs} Pairs`, endX - 8, summaryTop + 14, { align: 'right' });

    // Line 2: Price Bands & Total Price
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(0, 0, 0);

    const priceBands = [];
    for (let i = 1; i <= 5; i++) {
        const szStr = group[`Size${i}`];
        const prVal = group[`Price${i}`];
        if (szStr && prVal) {
            priceBands.push(`${szStr} = $${formatPrice(prVal)}`);
        }
    }

    let currentX = startX + 8;
    priceBands.forEach((bandStr) => {
        doc.text(bandStr, currentX, summaryTop + 28);
        currentX += 130;
    });

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(`Total = $${formatPrice(groupTotal)}`, endX - 8, summaryTop + 28, { align: 'right' });

    const endY = drawY + blockHeight;
    cursor.setY(endY);
    return endY;
};

export const drawProductTable = async (engine, cartItems = []) => {
    const groups = groupCartItemsBySku(cartItems);

    for (let index = 0; index < groups.length; index++) {
        const group = groups[index];

        // Dynamic page break check: ensures 108pt block + 8pt gap fits cleanly before 735pt limit
        engine.ensureSpace(116, { limit: 735, resetY: 36 });

        await drawSingleProductBlock(engine, group);
        engine.getCursor().space(8); // gap after block
    }

    return engine.getCursor().getY();
};
